
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useMusicPlayer } from '../../hooks/useMusicPlayer';

interface RadioStation {
  id: number;
  name: string;
  genre: string;
  description: string;
  imageUrl: string;
  listeners: string;
  currentTrack: {
    title: string;
    artist: string;
  };
  isLive: boolean;
}

const radioStations: RadioStation[] = [
  {
    id: 1,
    name: "SoundWave Pop Hits",
    genre: "Pop",
    description: "The biggest pop hits and latest chart-toppers",
    imageUrl: "https://readdy.ai/api/search-image?query=vibrant%20pop%20music%20radio%20station%20studio%20with%20colorful%20neon%20lights%2C%20modern%20broadcasting%20equipment%2C%20energetic%20atmosphere%2C%20professional%20radio%20setup&width=400&height=400&seq=radio-pop&orientation=squarish",
    listeners: "12.5K",
    currentTrack: {
      title: "Flowers",
      artist: "Miley Cyrus"
    },
    isLive: true
  },
  {
    id: 2,
    name: "Rock Classics 24/7",
    genre: "Rock",
    description: "Classic rock anthems and legendary hits",
    imageUrl: "https://readdy.ai/api/search-image?query=classic%20rock%20radio%20station%20with%20vintage%20equipment%2C%20guitar%20displays%2C%20dark%20atmospheric%20lighting%2C%20retro%20broadcasting%20setup%2C%20rock%20memorabilia&width=400&height=400&seq=radio-rock&orientation=squarish",
    listeners: "8.9K",
    currentTrack: {
      title: "Bohemian Rhapsody",
      artist: "Queen"
    },
    isLive: true
  },
  {
    id: 3,
    name: "Chill Vibes FM",
    genre: "Chill",
    description: "Relaxing beats and ambient sounds for focus",
    imageUrl: "https://readdy.ai/api/search-image?query=peaceful%20chill%20radio%20station%20with%20soft%20lighting%2C%20minimalist%20design%2C%20plants%2C%20cozy%20atmosphere%2C%20ambient%20music%20setup%2C%20zen%20broadcasting%20environment&width=400&height=400&seq=radio-chill&orientation=squarish",
    listeners: "15.2K",
    currentTrack: {
      title: "Weightless",
      artist: "Marconi Union"
    },
    isLive: true
  },
  {
    id: 4,
    name: "Hip Hop Central",
    genre: "Hip Hop",
    description: "Latest rap and hip hop from around the globe",
    imageUrl: "https://readdy.ai/api/search-image?query=urban%20hip%20hop%20radio%20station%20with%20graffiti%20art%2C%20modern%20mixing%20equipment%2C%20street%20culture%20aesthetic%2C%20professional%20broadcasting%20setup&width=400&height=400&seq=radio-hiphop&orientation=squarish",
    listeners: "11.7K",
    currentTrack: {
      title: "God's Plan",
      artist: "Drake"
    },
    isLive: true
  },
  {
    id: 5,
    name: "Electronic Pulse",
    genre: "Electronic",
    description: "Electronic dance music and techno beats",
    imageUrl: "https://readdy.ai/api/search-image?query=futuristic%20electronic%20music%20radio%20station%20with%20LED%20lights%2C%20synthesizers%2C%20digital%20displays%2C%20high-tech%20broadcasting%20equipment%2C%20cyberpunk%20aesthetic&width=400&height=400&seq=radio-electronic&orientation=squarish",
    listeners: "9.3K",
    currentTrack: {
      title: "Strobe",
      artist: "Deadmau5"
    },
    isLive: true
  },
  {
    id: 6,
    name: "Jazz Lounge",
    genre: "Jazz",
    description: "Smooth jazz and classic standards",
    imageUrl: "https://readdy.ai/api/search-image?query=elegant%20jazz%20radio%20station%20with%20warm%20lighting%2C%20vintage%20microphones%2C%20piano%2C%20sophisticated%20atmosphere%2C%20classic%20broadcasting%20setup&width=400&height=400&seq=radio-jazz&orientation=squarish",
    listeners: "6.8K",
    currentTrack: {
      title: "Take Five",
      artist: "Dave Brubeck"
    },
    isLive: true
  }
];

export default function RadioPage() {
  const [playingStation, setPlayingStation] = useState<number | null>(null);
  const { playTrack } = useMusicPlayer();
  const navigate = useNavigate();

  const handlePlayStation = (station: RadioStation) => {
    setPlayingStation(station.id);
    // Simulate playing the current track from the station
    playTrack({
      id: station.id,
      title: station.currentTrack.title,
      artist: station.currentTrack.artist,
      imageUrl: station.imageUrl,
      duration: "Live",
      album: station.name
    });
  };

  const handleStopStation = () => {
    setPlayingStation(null);
  };

  const handleTrackSelect = (track: any) => {
    playTrack(track);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onTrackSelect={handleTrackSelect} />
      
      <main className="pt-8 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-600 to-pink-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="mb-6">
              <i className="ri-radio-line text-6xl text-white mb-4 block"></i>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Live Radio Stations</h1>
            <p className="text-xl text-purple-100 mb-8">Tune in to live music from around the world</p>
            
            <div className="flex items-center justify-center space-x-6 text-white">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm">Live Broadcasting</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-headphone-line text-lg"></i>
                <span className="text-sm">64K+ Active Listeners</span>
              </div>
            </div>
          </div>
        </section>

        {/* Radio Stations Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {radioStations.map((station) => (
                <div 
                  key={station.id}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
                >
                  <div className="relative">
                    <img 
                      src={station.imageUrl}
                      alt={station.name}
                      className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <div className="flex items-center space-x-2 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-white text-xs font-medium">LIVE</span>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <div className="bg-black/50 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-white text-xs font-medium">{station.listeners} listeners</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{station.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{station.description}</p>
                      <span className="inline-block bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                        {station.genre}
                      </span>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                          <i className="ri-music-2-fill text-white text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">Now Playing</p>
                          <p className="text-sm text-gray-600 truncate">
                            {station.currentTrack.title} - {station.currentTrack.artist}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      {playingStation === station.id ? (
                        <Button
                          variant="secondary"
                          onClick={handleStopStation}
                          className="flex-1"
                        >
                          <i className="ri-stop-fill mr-2"></i>
                          Stop
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handlePlayStation(station)}
                          className="flex-1"
                        >
                          <i className="ri-play-fill mr-2"></i>
                          Listen Live
                        </Button>
                      )}
                      <button className="p-3 text-gray-400 hover:text-purple-600 transition-colors cursor-pointer">
                        <i className="ri-heart-line text-lg"></i>
                      </button>
                      <button className="p-3 text-gray-400 hover:text-purple-600 transition-colors cursor-pointer">
                        <i className="ri-share-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose SoundWave Radio?</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Experience the best in live radio with crystal clear audio and diverse programming</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hd-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">High Quality Audio</h3>
                <p className="text-gray-600">Crystal clear 320kbps streaming for the best listening experience</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Reach</h3>
                <p className="text-gray-600">Listen to stations from around the world, anytime, anywhere</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Broadcasting</h3>
                <p className="text-gray-600">Non-stop music and entertainment around the clock</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
